import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { TabReuseStrategy } from '../strategies/tab-reuse.strategy';

export interface AppTab {
    /** Unique ID — equals the first URL path segment (e.g. 'payments'). */
    id: string;
    /** Display label shown in the tab bar. */
    label: string;
    /** i18n key for the label (e.g. 'nav.payments'). Used for live translation. */
    labelKey: string;
    /** Angular route path (e.g. '/payments'). */
    route: string;
    /** PrimeNG icon class (e.g. 'pi-wallet'). */
    icon: string;
    /** Whether the tab shows a close button. */
    closable: boolean;
}

/** Hard cap on simultaneously open tabs. Oldest non-active tab auto-closes. */
const MAX_TABS = 12;

/**
 * Maps a tab ID (first URL path segment) to the localStorage key used by that
 * page to persist its search-form state. Add new entries here whenever a page
 * gains a pageStateStorageKey.
 */
const TAB_PAGE_STATE_KEYS: Record<string, string> = {
    'ledger':               'ledger-page-state-v1',
    'milk-bill':            'milk-bill-page-state-v1',
    'buyer-bill':           'buyer-bill-page-state-v1',
    'route-milk-receipt':   'route-milk-receipt-page-state-v1',
    'route-report':         'route-report-page-state-v1',
    'reports':              'reports-page-state-v1',
    'milk-collection':      'milk-collection-entry-draft-v1',
    'milk-sale':            'milk-sale-entry-draft-v1',
    'payments':             'payments-voucher-draft-v1',
    'receipts':             'receipts-voucher-draft-v1',
    'sales':                'sales-voucher-draft-v1',
};

/**
 * TabService — manages in-app tab state for web/desktop.
 *
 * Responsibilities:
 *  - Track the list of open tabs and the currently active tab.
 *  - Coordinate with TabReuseStrategy to preserve and destroy component trees.
 *  - Provide an API for ShellComponent to open/close/switch tabs.
 *
 * Mobile/native: isEnabled returns false, every call falls back to plain routing.
 */
@Injectable({ providedIn: 'root' })
export class TabService implements OnDestroy {

    private readonly destroy$ = new Subject<void>();
    private readonly tabsSubject = new BehaviorSubject<AppTab[]>([]);
    private readonly activeIdSubject = new BehaviorSubject<string>('');

    readonly tabs$ = this.tabsSubject.asObservable();
    readonly activeId$ = this.activeIdSubject.asObservable();

    get tabs(): AppTab[] { return this.tabsSubject.value; }
    get activeId(): string { return this.activeIdSubject.value; }

    /** True only on web/desktop (not native, not mobile viewport). */
    get isEnabled(): boolean {
        if (Capacitor.isNativePlatform()) { return false; }
        if (typeof window === 'undefined') { return false; }
        return window.matchMedia('(min-width: 769px)').matches;
    }

    constructor(
        private readonly router: Router,
        private readonly reuseStrategy: TabReuseStrategy,
    ) {
        // Keep activeId in sync when navigation happens (e.g. back/forward, programmatic nav)
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            takeUntil(this.destroy$),
        ).subscribe((e: any) => {
            this.syncActiveFromUrl(e.urlAfterRedirects || e.url);
        });
    }


    // ── Public API ───────────────────────────────────────────────────────────

    /**
     * Open (or re-activate) a tab and navigate to it.
     * Used by sidebar nav item and pinned-shortcut click handlers.
     */
    openTab(route: string, label: string, icon: string, labelKey: string = ''): void {
        if (!this.isEnabled) {
            this.router.navigate([route]);
            return;
        }

        const id = this.idFromRoute(route);
        const existing = this.tabs.find(t => t.id === id);

        if (existing) {
            // Tab already open — just switch to it
            this.setActive(id);
            this.router.navigate([existing.route]);
            return;
        }

        // Auto-evict oldest non-active tab if at max capacity
        if (this.tabs.length >= MAX_TABS) {
            const oldest = this.tabs.find(t => t.id !== this.activeId && t.closable);
            if (oldest) { this.removeTab(oldest.id); }
        }

        this.tabsSubject.next([
            ...this.tabs,
            { id, label, labelKey, route, icon, closable: true },
        ]);
        this.setActive(id);
        this.router.navigate([route]);
    }

    /**
     * Register a tab for the current route WITHOUT triggering a new navigation.
     * Called by ShellComponent when Angular navigates to a page via the URL bar,
     * a browser back/forward action, or programmatic router.navigate() outside
     * of the tab system — ensures those pages also get a tab entry.
     */
    addTabWithoutNavigating(route: string, label: string, icon: string, labelKey: string = ''): void {
        if (!this.isEnabled) { return; }

        const id = this.idFromRoute(route);
        if (this.tabs.find(t => t.id === id)) {
            // Tab already registered — just make it active
            this.setActive(id);
            return;
        }

        if (this.tabs.length >= MAX_TABS) {
            const oldest = this.tabs.find(t => t.id !== this.activeId && t.closable);
            if (oldest) { this.removeTab(oldest.id); }
        }

        this.tabsSubject.next([
            ...this.tabs,
            { id, label, labelKey, route, icon, closable: true },
        ]);
        this.setActive(id);
    }

    /**
     * Switch to an already-open tab and navigate to its route.
     * Used by the tab bar click handler.
     */
    activateTab(id: string): void {
        const tab = this.tabs.find(t => t.id === id);
        if (!tab) { return; }
        this.setActive(id);
        this.router.navigate([tab.route]);
    }

    /**
     * Close a tab and properly destroy its cached component tree.
     * If the closed tab was active, the adjacent tab (or dashboard) is activated.
     */
    closeTab(id: string): void {
        const idx = this.tabs.findIndex(t => t.id === id);
        if (idx === -1) { return; }

        const wasActive = this.activeId === id;
        const remaining = this.tabs.filter(t => t.id !== id);

        // Determine which tab gets focus after close (before mutating state)
        const nextTab = wasActive
            ? remaining[Math.max(0, idx - 1)] ?? remaining[0]
            : null;

        this.clearPageState(id);

        if (wasActive) {
            // Component is currently live (mounted). Only remove the stale cache entry
            // without destroying (Angular will destroy it during the navigation below).
            // preventDetach ensures it is NOT re-cached when Angular detaches it.
            this.reuseStrategy.removeCacheEntryOnly(id);
            this.reuseStrategy.preventDetach(id);
        } else {
            // Component is already detached in cache — safe to destroy and evict.
            this.reuseStrategy.clearCacheForRoute(id);
        }

        this.tabsSubject.next(remaining);

        if (wasActive) {
            if (nextTab) {
                this.setActive(nextTab.id);
                this.router.navigate([nextTab.route]);
            } else {
                this.activeIdSubject.next('');
                this.router.navigate(['/dashboard']);
            }
        }
    }

    /**
     * Destroy every open tab and clear all cached components.
     * Called by ShellComponent.ngOnDestroy() on logout to guarantee
     * that no stale component trees remain in memory.
     */
    closeAll(): void {
        this.reuseStrategy.clearAllCache();
        // Clear persisted page-state for every open tab
        this.tabs.forEach(t => this.clearPageState(t.id));
        this.tabsSubject.next([]);
        this.activeIdSubject.next('');
    }

    // ── Lifecycle ────────────────────────────────────────────────────────────

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Private helpers ──────────────────────────────────────────────────────

    private setActive(id: string): void {
        this.activeIdSubject.next(id);
    }

    /**
     * Extract a stable tab ID from a route path.
     * '/payments/new?date=today' → 'payments'
     */
    private idFromRoute(route: string): string {
        return route.replace(/^\//, '').split(/[/?#]/)[0];
    }

    /** Remove a tab from the list and clean up its cache. */
    private removeTab(id: string): void {
        this.reuseStrategy.preventDetach(id);
        this.reuseStrategy.clearCacheForRoute(id);
        this.clearPageState(id);
        this.tabsSubject.next(this.tabs.filter(t => t.id !== id));
    }

    /** Clear the persisted localStorage search-form state for a tab, if any.
     *  Re-clears on the next macrotask to catch any synchronous late writes
     *  from the dying component during Angular's destroy cycle. */
    private clearPageState(id: string): void {
        const key = TAB_PAGE_STATE_KEYS[id];
        if (!key) { return; }
        localStorage.removeItem(key);
        setTimeout(() => localStorage.removeItem(key), 0);
        setTimeout(() => localStorage.removeItem(key), 300);
    }

    /** Sync the activeId BehaviorSubject from an incoming URL (after navigation). */
    private syncActiveFromUrl(url: string): void {
        if (!this.isEnabled) { return; }
        const id = this.idFromRoute(url.split('?')[0]);
        const tab = this.tabs.find(t => t.id === id);
        if (tab && tab.id !== this.activeId) {
            this.activeIdSubject.next(tab.id);
        }
    }
}
